import { useState } from "react";
import OffCanvas from "../../../components/offCanvas";
import { AddMedication } from "./AddMedication";
import CustomButtonRenderer from "../../../renderers/CustomButtonRenderer";
import { formatDateTime } from "../../schedule/dateFormat";
import { MedicationHeader } from "./MedicationHeader";
import {
  FilterCriteria,
  GetPatientMedicationMapping,
  GridDataRequest,
  GridDataResponse,
} from "patients-experience-client";
import Toaster, { ToastConfig } from "../../../components/Toaster";
import { ErrorTypes } from "../../../interfaces/errors";
import { GenericGrid } from "../../../grid/GenericGrid";
import {
  ColDef,
  ValueFormatterParams,
  ValueGetterParams,
} from "ag-grid-community";
import { DataProvider } from "../../../grid/GridDataAdaptor";
import { formatDate, formatDateTask } from "../../../helper/dateFormatter";
import Lookups from "../../../constants/Lookups";
import { LookupCellValueGetter } from "../../../grid/renderers/LookupCellValueGetter";
import { createGridApi } from "../../../api-helpers/GridApiHelper";

interface MedicationProps {
  patientId: string;
}

export interface Props {
  colDefs: ColDef[];
  dataProvider: DataProvider;
  filters: FilterCriteria[];
}

export const Medications = (props: MedicationProps) => {
  const gridApi = createGridApi();
  const [selectedMedication, setSelectedMedication] = useState(
    {} as GetPatientMedicationMapping,
  );
  const [showToast, setShowToast] = useState(false);
  const [gridReloadKey, setGridReloadKey] = useState(0);
  const [filters, setFilters] = useState<FilterCriteria[]>([]);
  const [offCanvasTitle, setOffCanvasTitle] = useState("");
  const [offCanvasShow, setOffCanvasShow] = useState(false);
  const [offCanvasComp, setOffCanvasComp] = useState("");
  const [toastConfig, setToastConfig] = useState<ToastConfig>({
    type: "success" as ErrorTypes,
    title: "",
    icon: "",
  });

  const handleAddUpdateMedicationForm = async (toasterConfig: ToastConfig) => {
    setToastConfig(toasterConfig);
    setShowToast(true);
  };

  const loadData = async (
    request: GridDataRequest,
  ): Promise<GridDataResponse> => {
    return (await gridApi.gridControllerGetMedicationGridData(request)).data;
  };

  const reloadGrid = () => {
    setGridReloadKey(gridReloadKey + 1);
  };

  const handleEditMedication = (medication: GetPatientMedicationMapping) => {
    setSelectedMedication(medication);
    handleOffCanvasShow("editMedication");
  };

  const handleOffCanvasShow = (comp: string) => {
    if (comp === "addMedication") {
      setOffCanvasTitle("New Medication");
    }
    if (comp === "editMedication") {
      setOffCanvasTitle("Edit Medication");
    }

    setOffCanvasComp(comp);
    setOffCanvasShow(!offCanvasShow);
  };

  const colDefs: ColDef[] = [
    {
      field: "medicationName",
      headerName: "Name",
      minWidth: 300,
      pinned: "left",
      sortable: true,
    },
    {
      field: "dosageRecommended",
      headerName: "Dosage",
      minWidth: 100,
      maxWidth: 120,
      valueGetter: (params) => params.data.dosageRecommended ?? "-",
      sortable: true,
    },

    {
      field: "medicationStartDate",
      headerName: "From Date",
      minWidth: 150,
      maxWidth: 150,
      valueFormatter: function (params: any) {
        return params.data?.medicationStartDate
          ? formatDateTask(params.data.medicationStartDate)
          : "-";
      },
      sortable: true,
    },

    {
      field: "medicationEndDate",
      headerName: "To Date",
      minWidth: 150,
      maxWidth: 150,
      valueFormatter: function (params: any) {
        return params.data?.medicationEndDate
          ? formatDateTask(params.data.medicationEndDate)
          : "-";
      },
      sortable: true,
    },

    {
      field: "addedBy_name",
      headerName: "Added By",
      minWidth: 120,
      sortable: true,
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 60,
      maxWidth: 120,
      sortable: true,
      cellRenderer: (params: ValueFormatterParams) => {
        const status = params.data.status;
        if (status === "Active") {
          return <span className="badge background-completed">{status}</span>;
        }
        if (status === "Inactive") {
          return <span className="badge background-cancelled">{status}</span>;
        }
        if (status === "Unknown") {
          return <span className="badge background-cancelled">{status}</span>;
        }
        if (status === "Stopped") {
          return <span className="badge background-cancelled">{status}</span>;
        }
        if (status === "Completed") {
          return <span className="badge background-cancelled">{status}</span>;
        }
        if (status === "On-Hold") {
          return <span className="badge background-cancelled">{status}</span>;
        }
        return <span className="">{"-"}</span>;
      },
    },
    {
      field: "medRecStatus",
      headerName: "Med Rec Status",
      minWidth: 250,
      maxWidth: 300,
      sortable: true,
      cellRenderer: (params: ValueFormatterParams) => {
        const status = params.data.medRecStatus;
        if (status === "Reviewed") {
          return <span className="badge background-completed">{status}</span>;
        }
        if (status === "Not Reviewed") {
          return <span className="badge background-cancelled">{status}</span>;
        }
        return <span className="">{"-"}</span>;
      },
    },
    {
      field: "dispensed",
      headerName: "Dispensed",
      minWidth: 130,
      maxWidth: 130,
      valueGetter: (params) => params.data.dispensed ?? "-",
      sortable: true,
    },
    {
      field: "prescribedAt",
      headerName: "Last Prescribed",
      minWidth: 300,
      maxWidth: 350,
      valueFormatter: function (params: any) {
        const prescribedAt = params.data?.prescribedAt
          ? formatDateTime(params.data.prescribedAt)
          : null;
        const prescribedBy = params.data?.prescribedBy || null;
        if (!prescribedAt && !prescribedBy) {
          return "-";
        }
        return `${prescribedAt || "-"}\n${prescribedBy || "-"}`;
      },
      sortable: true,
      // cellStyle: { 'white-space': 'pre', 'line-height': '1.3' }
    },
    {
      field: "lastFilled",
      headerName: "Last Filled",
      minWidth: 100,
      maxWidth: 150,
      valueFormatter: (params: any) => {
        return params.data?.lastFilled
          ? formatDate(params.data.lastFilled)
          : "-";
      },
      sortable: true,
    },

    {
      cellRenderer: CustomButtonRenderer,
      cellRendererParams: {
        onClick: handleEditMedication,
      },
      maxWidth: 50,
      pinned: "right",
      sortable: false,
    },
  ];

  return (
    <>
      <div className="row">
        <Toaster
          show={showToast}
          onClose={() => setShowToast(false)}
          type={toastConfig.type}
          title={toastConfig.title}
          message={toastConfig.message}
          icon={toastConfig.icon}
        />
      </div>
      <MedicationHeader
        patientId={props.patientId}
        offCanvasShowCallback={handleOffCanvasShow}
        onUpdateFilters={setFilters}
      />
      <div className="medication-table">
        <GenericGrid
          gridId="medications-history-grid"
          colDefs={colDefs}
          dataProvider={loadData}
          filters={filters}
          fixedHeight="350px"
          key={gridReloadKey}
          pagination={false}
        ></GenericGrid>
      </div>
      <OffCanvas
        title={offCanvasTitle}
        offCanvasShow={offCanvasShow}
        setOffCanvasShow={setOffCanvasShow}
        offCanvasComp={offCanvasComp}
      >
        {offCanvasComp === "addMedication" && (
          <AddMedication
            patientId={props.patientId}
            setOffCanvasShow={setOffCanvasShow}
            reloadGrid={reloadGrid}
            onSubmitMedication={handleAddUpdateMedicationForm}
          />
        )}

        {offCanvasComp === "editMedication" && (
          <AddMedication
            patientId={props.patientId}
            setOffCanvasShow={setOffCanvasShow}
            reloadGrid={reloadGrid}
            onSubmitMedication={handleAddUpdateMedicationForm}
            selectedMedication={selectedMedication}
          />
          // <EditMedication
          //   patientId= {props.patientId}
          //   setOffCanvasShow={setOffCanvasShow}
          //   selectedMedication={selectedMedication}
          //   reloadGrid={getMedicationHistory}
          //   onSubmitMedication={handleAddUpdateMedicationForm}
          // />
        )}
      </OffCanvas>
    </>
  );
};
